import { BoxProps } from '@mui/material'

import React, { useRef, useEffect, useState } from 'react'
import { red } from '@mui/material/colors'
import { CrossWrapper, SvgContainer } from './styles'

interface Props extends Omit<BoxProps, 'children' | 'onSelect'> {
  children: React.ReactElement
  lineColor?: string
  lineThickness?: number
  strikethrough?: {
    isStrikethrough: boolean
    isEnabled: boolean
    toggle: (identifier: string) => void
  }
  selected?: boolean
  onSelect?: (id: string) => void
  offset?: number
}

export const CrossContainer: React.FC<Props> = ({
  children,
  className,
  lineColor = red[400],
  lineThickness = 2,
  strikethrough,
  selected = false,
  onSelect,
  offset = 12,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const childRef = useRef<HTMLElement>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    const updateDimensions = () => {
      if (childRef.current) {
        const { width, height } = childRef.current.getBoundingClientRect()
        setDimensions({ width, height })
      }
    }

    updateDimensions()

    const resizeObserver = new ResizeObserver(updateDimensions)
    if (childRef.current) {
      resizeObserver.observe(childRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const childWithRef = React.cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      childRef.current = node

      const originalRef = (children as any).ref

      if (!originalRef) return

      if (typeof originalRef === 'function') {
        originalRef(node)
        return
      }

      originalRef.current = node
    },
  })

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!strikethrough) return

    if (strikethrough.isEnabled) {
      e.stopPropagation()
      if (selected) onSelect?.(props.id)
      strikethrough.toggle(props.id)
      return
    }
  }

  // Eliminations persist after the tool is switched off — that is the point of the feature:
  // strike some answers, turn the tool off, then pick from what is left.
  const shouldShowCross = !!strikethrough?.isStrikethrough || !!(strikethrough?.isEnabled && isHovered)
  const crossColor = strikethrough?.isStrikethrough ? lineColor : red[200]
  const crossOpacity = strikethrough?.isStrikethrough ? 1 : 0.4

  return (
    <CrossWrapper
      ref={containerRef}
      className={className}
      role='presentation'
      onClickCapture={onClick}

      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      {childWithRef}

      {shouldShowCross && dimensions.width > 0 && dimensions.height > 0 && (
        <SvgContainer
          width={dimensions.width}
          height={dimensions.height}
          style={{ opacity: crossOpacity }}
          aria-hidden
        >
          <svg
            width={dimensions.width}
            height={dimensions.height}
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
          >
            <line
              x1={offset}
              y1={offset}
              x2={dimensions.width - offset}
              y2={dimensions.height - offset}
              stroke={crossColor}
              strokeWidth={lineThickness}
              strokeLinecap='round'
            />
            <line
              x1={dimensions.width - offset}
              y1={offset}
              x2={offset}
              y2={dimensions.height - offset}
              stroke={crossColor}
              strokeWidth={lineThickness}
              strokeLinecap='round'
            />
          </svg>
        </SvgContainer>
      )}
    </CrossWrapper>
  )
}
